import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setIsValid(false);
        setErrorMessage("No reset token provided");
        return;
      }

      try {
        await axios.get(`${API}/api/auth/verify-reset-token/${token}`);
        setIsValid(true);
      } catch (error) {
        setIsValid(false);
        setErrorMessage(error.response?.data?.detail || "Invalid or expired reset link");
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setIsSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isVerifying) {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      );
    }

    if (!isValid) {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="rounded-full bg-red-100 p-4">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-red-600">Invalid Reset Link</h3>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
          </div>
          <div className="space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full" data-testid="request-new-link-button">
                Request New Reset Link
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="w-full" data-testid="back-to-login-button">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    if (isSuccess) {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="rounded-full bg-green-100 p-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-green-600">Password Reset Successful!</h3>
            <p className="text-sm text-muted-foreground">
              Your password has been changed. You can now log in with your new password.
            </p>
          </div>
          <Button
            className="w-full h-12 rounded-full"
            onClick={() => navigate("/login")}
            data-testid="go-to-login-button"
          >
            Go to Login
          </Button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              required
              minLength={6}
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="toggle-password-visibility"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10 pr-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              required
              minLength={6}
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="toggle-confirm-password-visibility"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Password must be at least 6 characters long.
        </p>
        <Button
          type="submit"
          className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          disabled={isLoading}
          data-testid="reset-password-button"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Resetting...</span>
            </div>
          ) : (
            <span>Reset Password</span>
          )}
        </Button>
        <div className="text-center">
          <Link 
            to="/login" 
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
        </div>
      </form>
    );
  };

  return (
    <div className="auth-split">
      {/* Left Side - Image */}
      <div 
        className="auth-image relative hidden md:flex items-center justify-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1710020319182-3102611ed86b?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#663399]/80"></div>
        <div className="relative z-10 text-white p-12 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/images/logo.png" alt="Future Owners Logo" className="w-16 h-16 object-contain" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
          </div>
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Create New Password
          </h2>
          <p className="text-lg text-white/80">
            Choose a strong password to keep your account secure.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex items-center justify-center p-8 bg-gradient-to-b from-white to-secondary/20">
        <Card className="w-full max-w-md border-border/50 shadow-soft" data-testid="reset-password-card">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/images/logo.png" alt="Future Owners Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
            </div>
            <CardTitle className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Reset Password
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {isValid && !isSuccess ? "Enter your new password below" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
